import { useEffect } from 'react';

/**
 * Injects JSON-LD structured data scripts directly into document.head.
 * react-helmet-async can drop/dedupe ld+json scripts; this bypasses that.
 *
 * Defensive: any null/undefined entries (or non-object values) in `schemas`
 * are filtered out. Without this filter, callers that conditionally include
 * a schema (e.g. `[main, optional || null, breadcrumb]`) would emit a
 * <script type="application/ld+json">null</script> block, which Google
 * Search Console flags as `Invalid top level element "null"`.
 *
 * @param {Array} schemas - Array of schema objects to inject
 * @param {string} pageId - Unique identifier for cleanup
 */
const useJsonLd = (schemas, pageId) => {
  useEffect(() => {
    const valid = Array.isArray(schemas)
      ? schemas.filter((s) => s !== null && s !== undefined && typeof s === 'object')
      : [];
    const ids = valid.map((schema, i) => {
      const id = `jsonld-${pageId}-${i}`;
      let el = document.getElementById(id);
      if (!el) {
        el = document.createElement('script');
        el.type = 'application/ld+json';
        el.id = id;
        document.head.appendChild(el);
      }
      el.textContent = JSON.stringify(schema);
      return id;
    });

    return () => {
      ids.forEach(id => {
        const el = document.getElementById(id);
        if (el) el.remove();
      });
    };
  }, [schemas, pageId]);
};

export default useJsonLd;
